import { axiosApi } from '@accor/customer-core'
import {
  API_GET_COMPANY_BY_ID,
  API_COMPANY_EMPLOYEE,
  API_COMPANY_FILES,
  API_PATCH_COMPANY_BY_ID,
  API_GET_COMPANY_SETTINGS,
  POST_COMPANY_TRANSACTIONS
} from '@/constants/urls'
import { logger } from '@/services/logger'
import type { AxiosResponse } from 'axios'
import type {
  COMPANY_ACCOUNT_TYPE,
  COMPANY_ADDRESS_USAGE,
  COMPANY_CATEGORY,
  COMPANY_NUMBER_OF_EMPLOYEES,
  COMPANY_TYPE
} from '@/constants/company'

export interface Employee {
  pmid: string
  professionalEmail: string
  personalInfo: {
    firstName: string
    lastName: string
    jobTitle?: string
    jobRole?: string
  }
  authorization: {
    role: string
    permissions: string[]
  }
  company: {
    id: string
    name: string
  }
  status: string
}

export interface CompanyFile {
  id: string
  uploadDateTime: string
  type: string
  name: string
  url: string
}

export interface CompanyType {
  id: string
  name: string
  displayName: string
  recordType: string
  addresses: Address[]
  status?: string
  accountManagementType?: string
  businessLine: string
  businessStatus: string
  businessType: string
  classificationLabel: string
  closingReason: string
  mainActivity?: string
  mainDivision: string
  nationalId?: string
  noContracting: boolean
  accountType: COMPANY_ACCOUNT_TYPE
  comments?: string
  isAutoGeneration?: boolean
  isGlobalMaster?: boolean
  globalMasterCompanyId: string
  isSubsidiary?: boolean
  businessCreationYear?: number
  companyStatus?: string
  leadSource?: string
  category?: COMPANY_CATEGORY
  type?: COMPANY_TYPE
  rsuId: string
  numberOfEmployees: COMPANY_NUMBER_OF_EMPLOYEES
  domains: string[]
  entities?: string[]
  isCompanyLoyaltyMember?: boolean
  loyalty?: Loyalty
  tracking?: Tracking
}

export interface Address {
  address1: string
  address2?: string
  address3?: string
  poBox?: string
  city: string
  zipCode: string
  stateCode: string | null
  countryCode: string
  usageType?: COMPANY_ADDRESS_USAGE
}

export interface PointExpiry {
  points: string
  date: string
}

export interface MaxCurrencyValue {
  value: number
  currency: string
  formattedValue: string
}

export interface LoyaltyBalance {
  points: string
  pointExpiry: PointExpiry[]
  maxCurrencyValue?: MaxCurrencyValue
}

export interface Loyalty {
  id?: string
  programId?: string
  programName: string
  programStatus: string
  invitationLink?: string
  balance?: LoyaltyBalance
}
export interface Tracking {
  createdDate?: string
  creationSystem?: string
  createdBy?: string
  updatedDate?: string
  updatedSystem?: string
  updatedBy?: string
}

export enum OrderingParam {
  DESC = 'DESC',
  ASC = 'ASC'
}

export enum OperationType {
  CREDIT = 'credit',
  DEBIT = 'debit'
}

export interface CompanyTransactionsRequestBody {
  pageSize?: number
  nextPageToken?: string
  sort?: {
    field: string
    ordering: OrderingParam
  }
  filters?: {
    category?: string
    operationType?: OperationType
    transactionDate?: {
      min: string
      max: string
    }
  }
}

export interface OperationAnimation {
  code: string
  name: string
  type: string
}

export interface OperationCampaign {
  code: string
  name: string
}

export interface OperationEmployee {
  lastName?: string
  firstName?: string
  pmid: string
}

export interface HotelInfo {
  code: string
  name: string
}

export interface Operation {
  label: string
  id: number
  operationClass: string
  credit: boolean
  points: number
  creationDate: string
  animation: OperationAnimation
  campaign: OperationCampaign
  employee: OperationEmployee
}

export interface CompanyTransactionTotal {
  credit: {
    points: number
  }
  debit: {
    points: number
  }
}

export interface CompanyTransaction {
  category: string
  transactionDate: string
  relatedStay: {
    hotel: HotelInfo
    orderId: string
    checkinDate: string
    checkoutDate: string
    nbNights: number
  }
  total: CompanyTransactionTotal
  operations: Operation[]
}

export interface CompanyTransactionsByCompanyIdResponse {
  transactions: CompanyTransaction[]
  nextPageToken: string
}

export type CompanySettingsType = Record<string, unknown>

export const getCompanyById = async (
  companyId: string,
  currency?: string
): Promise<AxiosResponse<CompanyType>> =>
  await axiosApi.get(`${API_GET_COMPANY_BY_ID(companyId)}`, {
    params: {
      currency
    }
  })

export const getEmployee = async (): Promise<Employee | undefined> => {
  try {
    const { data } = await axiosApi.get(API_COMPANY_EMPLOYEE)
    return data
  } catch (err: any) {
    logger.error(err)
  }
}

export const getCompanyFiles = async (companyId: string): Promise<CompanyFile[]> => {
  try {
    const { data } = await axiosApi.get(API_COMPANY_FILES(companyId))
    return data ?? []
  } catch (err: any) {
    logger.error(err)
    return []
  }
}

export const getTravelPolicyUrl = async (companyId: string): Promise<string> => {
  const files = await getCompanyFiles(companyId)
  const travelPolicy = files.find((file) => file.type === 'travel_policy')
  return travelPolicy?.url ?? ''
}
export const patchCompany = async (
  companyId: string,
  company: Partial<CompanyType>
): Promise<AxiosResponse<CompanyType>> =>
  await axiosApi.patch(`${API_PATCH_COMPANY_BY_ID(companyId)}`, company)

export const getCompanySettings = async (
  companyId: string
): Promise<AxiosResponse<CompanySettingsType>> =>
  await axiosApi.get(`${API_GET_COMPANY_SETTINGS(companyId)}`)

export const updateCompanySettings = async (
  companyId: string,
  companySettings: CompanySettingsType
): Promise<AxiosResponse<void>> =>
  await axiosApi.put(`${API_GET_COMPANY_SETTINGS(companyId)}`, companySettings)

export const getCompanyTransactionsByCompanyId = async (
  companyId: string,
  params?: CompanyTransactionsRequestBody,
  signal?: AbortSignal
): Promise<AxiosResponse<CompanyTransactionsByCompanyIdResponse>> =>
  await axiosApi.post(POST_COMPANY_TRANSACTIONS(companyId), params, { signal })
